import React from 'react';
import PropTypes from 'prop-types';
import Button from '~/modules/components/global/button';
import Icon from '~/modules/components/global/icon';
import TwoFactorApp from '~/modules/components/global/preferences/two-factor-app';
import { useTwoFactorStatus } from '~/modules/hooks/use-two-factor';
import { ProfilePreferencesSmallLegend } from '~/modules/components/global/preferences/preferences.css';
import {
	MethodAction,
	MethodControls,
	MethodInfo,
	MethodPanel,
	MethodRow,
	StateChip
} from '~/modules/components/global/preferences/two-factor-app.css';

/**
 * Two-factor authentication: one setting, with its methods boxed together underneath.
 *
 * It used to be a bare checkbox, and an authenticator section beside it would have been a second
 * switch for a second mechanism with nothing on screen saying which one wins. Every comparable
 * product — GitHub, Google, Microsoft, AWS — models this as one state you turn on by adding a
 * method, and that is what this is.
 *
 * The state beside the heading is *derived* and read-only: `Users::hasTwoFactor()` computes the
 * same answer server-side (enforced, or a confirmed authenticator, or the email preference), so
 * this is that answer rendered rather than a third thing to keep in step. Each row below owns one
 * method and neither writes the other's flag.
 *
 * It lives in its own `fieldset` because the profile fields follow it, and `ProfileContainer`
 * spaces fieldsets apart — without that the method descriptions ran straight into the "Name"
 * label below.
 */
const TwoFactorSection = ({ preferences, handlePreferencesChange, user }) => {
	const { status } = useTwoFactorStatus();

	const enforced = !!user?.hasEnforced2fa;
	// Two facts about the authenticator, because the routes read them differently. It is
	// *enrolled* when a confirmed credential exists, and every passwordless route -- the emailed
	// sign-in link, an accepted invitation -- asks for its code on that fact alone
	// (`Session::_admitProvenAddress()` uses `enrolledFor()`), so two-factor is on whenever it is
	// enrolled. It is *in force at the password form* only while `auth-allow-totp` allows it:
	// with the flag off the API answers `configured: false` and keeps `confirmed: true` so the
	// credential can still be removed, and a password sign-in falls back to the emailed challenge.
	const enrolled = !!status?.confirmed;
	const appTakesPasswordSignIn = enrolled && status?.configured !== false;
	const emailEnabled = enforced || !!preferences?.twofactor?.enabled;
	const on = enforced || enrolled || emailEnabled;

	// Never reached while an app is enrolled and in force, whatever the preference underneath
	// says: the app takes the sign-in outright and nothing is emailed. So the row reports one
	// state and offers no action -- "Enable" would have invited a click that changes nothing
	// anybody can observe. With the app switched off for the password form the preference is
	// read again, so the row reports it and offers to change it, and says which sign-ins still
	// ask the app for a code.
	const emailUnused = appTakesPasswordSignIn;
	const appSwitchedOff = enrolled && !appTakesPasswordSignIn;

	return (
		<fieldset>
			<legend className="h5" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
				Two Factor Authentication
				<StateChip on={on} data-testid="two-factor-state">
					{on ? 'On' : 'Off'}
				</StateChip>
				{enforced
					? <ProfilePreferencesSmallLegend>
						<Icon use="warning" color={'silver-base'}/>&nbsp;
						Required by your organization
					</ProfilePreferencesSmallLegend>
					: ''}
			</legend>

			<MethodPanel>
				<TwoFactorApp/>

				<MethodRow>
					<MethodInfo>
						<strong>Email link</strong>
						<span data-testid="two-factor-email-note">
							{emailUnused
								? 'Your authenticator app handles sign-in. This applies again if '
									+ 'you turn the app off'
								: (appSwitchedOff
									? 'Your authenticator app is switched off for password sign-ins, '
										+ 'which use this link instead. Sign-in links and invitations '
										+ 'still ask for its code.'
									: 'A link is emailed to you when you sign in')}
						</span>
					</MethodInfo>
					{/* Same two columns as the row above, so the states read down one column and
					    the controls down another. */}
					<MethodControls>
						{/*
							"Unused" covers both underlying values once an app is enrolled,
							because that is what the server does: `Session::_challenge()` mails
							nothing when a confirmed authenticator exists, so this method is not
							a fallback -- it is simply not consulted. Reporting "On" or "Off"
							here would be describing a setting nothing reads.
						*/}
						<StateChip
							on={emailEnabled && !emailUnused}
							data-testid="two-factor-email-state"
						>
							{emailUnused ? 'Unused' : (emailEnabled ? 'On' : 'Off')}
						</StateChip>
						<MethodAction>
							{/*
								A button rather than a checkbox so the row reads the same as the
								authenticator above it -- same weights for the same meaning, On
								switched off by a secondary button and Off switched on by a
								primary one. Disabled rather than hidden when the studio enforces
								two-factor: the state is still worth showing.
							*/}
							<Button
								variant={emailEnabled ? 'secondary' : 'primary'}
								size="sm"
								type="button"
								data-testid="two-factor-email-toggle"
								disabled={enforced || emailUnused}
								onClick={() => handlePreferencesChange('twofactor', 'enabled', !emailEnabled)}
							>
								{emailEnabled ? 'Disable' : 'Enable'}
							</Button>
						</MethodAction>
					</MethodControls>
				</MethodRow>
			</MethodPanel>

			{on
				? null
				: (
					<ProfilePreferencesSmallLegend style={{ padding: 0 }}>
						Without a second factor, one credential is all that protects this account:
						whoever can sign in as you once, is you.
					</ProfilePreferencesSmallLegend>
				)}
		</fieldset>
	);
};

TwoFactorSection.propTypes = {
	preferences: PropTypes.object,
	handlePreferencesChange: PropTypes.func.isRequired,
	user: PropTypes.object
};

export default TwoFactorSection;
